import { Component } from '@theme/component';

/** Reset restored or layout-shifted TAV rows once, without overriding a visitor's interaction. */
class TavRowStart extends Component {
  #controller;
  #observer;
  #frame = 0;
  #interacted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  async #connect() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#interacted = this.hasAttribute('data-tav-user-interacted');
    const controller = new AbortController();
    this.#controller = controller;
    for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin']) {
      this.addEventListener(type, this.#onInteraction, { capture: true, passive: true, signal: controller.signal });
    }
    window.addEventListener('pageshow', this.#onPageShow, { signal: controller.signal });
    await customElements.whenDefined('slideshow-component');
    if (controller.signal.aborted || !this.isConnected || this.#interacted) return;
    this.#prepare();
  }

  #onInteraction = (event) => {
    if (!event.isTrusted) return;
    this.#interacted = true;
    this.setAttribute('data-tav-user-interacted', '');
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  };

  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.removeAttribute('data-tav-user-interacted');
    this.#interacted = false;
    this.#prepare();
  };

  #prepare() {
    const carousel = this.querySelector('.tav-row__carousel');
    if (!(carousel instanceof HTMLElement)) return;
    this.#queueReset();
    this.#observer?.disconnect();
    if (carousel.hasAttribute('in-viewport')) return;
    // The theme enables horizontal scrolling only when the row enters view.
    this.#observer = new MutationObserver(() => {
      if (!carousel.hasAttribute('in-viewport')) return;
      this.#observer?.disconnect();
      this.#queueReset();
    });
    this.#observer.observe(carousel, { attributes: true, attributeFilter: ['in-viewport'] });
  }

  #queueReset() {
    if (this.#interacted) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.#reset();
      this.#frame = requestAnimationFrame(() => this.#reset());
    });
  }

  #reset() {
    if (!this.isConnected || this.#interacted) return;
    const carousel = this.querySelector('.tav-row__carousel');
    const scroller = carousel?.querySelector('slideshow-slides');
    if (!(scroller instanceof HTMLElement) || !carousel || !('current' in carousel)) return;
    scroller.scrollTo({ left: 0, behavior: 'instant' });
    carousel.current = 0;
  }
}

if (!customElements.get('tav-row-start-component')) {
  customElements.define('tav-row-start-component', TavRowStart);
}
